import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  railBg: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  successBg: "#eefaf2",
  successBorder: "#b7e4c4",
  successText: "#17733b",
};

const OrganizationHeader = ({
  busy,
  dirty,
  disabled,
  invalid,
  onRestore,
  onSave,
}) => {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "stretch", md: "flex-end" }}
      justifyContent="space-between"
      spacing={1.15}
      sx={{ mb: 1.2 }}
    >
      <Box>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 19, md: 20.5 },
            lineHeight: 1.2,
            fontWeight: 600,
            letterSpacing: "-0.018em",
            color: ui.text,
          }}
        >
          <FormattedMessage id="admin.organizationDetails" />
        </Typography>
        <Typography sx={{ mt: 0.3, color: ui.muted, fontSize: 12.2, lineHeight: 1.45 }}>
          <FormattedMessage id="admin.organizationDetails.subTitle" />
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={0.7}
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          width: { xs: "100%", md: "auto" },
          flexWrap: "wrap",
          rowGap: 0.6,
        }}
      >
        {dirty && !busy && (
          <Typography sx={{ fontSize: 10.8, color: ui.muted, whiteSpace: "nowrap" }}>
            <FormattedMessage id="admin.organizationDetails.unsavedChanges" />
          </Typography>
        )}
        {dirty && (
          <Button
            disabled={disabled || busy}
            onClick={onRestore}
            size="small"
            variant="text"
            sx={{
              minHeight: { xs: 40, sm: 32 },
              px: 0.9,
              textTransform: "none",
              fontSize: 11.7,
            }}
          >
            <FormattedMessage id="btn.reset" />
          </Button>
        )}
        <Button
          disabled={!dirty || disabled || busy || invalid}
          onClick={onSave}
          size="small"
          startIcon={busy ? <CircularProgress color="inherit" size={12} /> : <SaveOutlinedIcon sx={{ fontSize: 14 }} />}
          variant="contained"
          sx={{
            minHeight: { xs: 40, sm: 34 },
            minWidth: { xs: 150, sm: 0 },
            flex: { xs: "1 1 150px", sm: "0 0 auto" },
            px: 1.25,
            borderRadius: 0.7,
            fontSize: 11.8,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          <FormattedMessage id="btn.saveChanges" />
        </Button>
      </Stack>
    </Stack>
  );
};

OrganizationHeader.propTypes = {
  busy: PropTypes.bool,
  dirty: PropTypes.bool,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  onRestore: PropTypes.func,
  onSave: PropTypes.func,
};

OrganizationHeader.defaultProps = {
  busy: false,
  dirty: false,
  disabled: false,
  invalid: false,
  onRestore: undefined,
  onSave: undefined,
};

OrganizationHeader.displayName = "OrganizationHeader";

export default OrganizationHeader;
